import { HttpParams } from '@angular/common/http';
import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { SelectListItem } from '@app/shared/models';
import { ConstrainedField } from '@app/shared/models/constrained-field';
import { ServiceBase, SnackbarFactory } from '@app/shared/services';
import { catchError, map, Observable, of } from 'rxjs';

@Component({
  selector: 'app-selectlist-cell',
  templateUrl: './selectlist-cell.component.html',
  styleUrls: ['./selectlist-cell.component.scss']
})
export class SelectlistCellComponent implements OnInit, OnChanges {
  @Input() public field!: ConstrainedField;
  @Input() public params!: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> };
  public selectItems$: Observable<SelectListItem[]>;
  public ddlMultipleText: string;

  protected service = inject(ServiceBase);
  protected snackBar = inject(SnackbarFactory);

  public toDbModel(value: SelectListItem | SelectListItem[]): number | string | (string | number)[] {
    if (this.field.controlType === "ddl" && !Array.isArray(value)) {
      return value?.id ?? null
    };

    if (Array.isArray(value)) {
      return value.map((item: SelectListItem) => item?.id).join(',');
    };

    return null;
  }

  public ngOnInit(): void {
    this.setSelectItems();

    if (this.field.controlType === "ddlMultiple") {
      this.field.control.valueChanges.subscribe((items: SelectListItem[]) => {
        this.ddlMultipleText = items?.length > 0 ? items[0].text : '';
      })
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.params && !changes.params.firstChange) {
      this.setSelectItems();
    }
  }

  setSelectItems() {
    if (this.field.control.enabled) {
      this.field.control.disable({ emitEvent: false });
    }

    this.selectItems$ = this.service
      .getRefSelectList(`selectlist/${this.field.key}`, this.params)
      .pipe(
        catchError((err) => {
          this.snackBar.showButtonSnackbar(err);
          if (this.field.editable) {
            this.field.control.enable({ emitEvent: false });
          }
          return of({ selectItems: [] as SelectListItem[], defaultItem: null });
        }),
        map(({ selectItems, defaultItem }) => {
          if (this.field.editable) {
            this.field.control.enable({ emitEvent: false });
          }

          if (this.field.control.value) {
            if (this.field.controlType == "ddl") {
              if (typeof this.field.control.value === "string" || typeof this.field.control.value === "number") {
                this.field.control.setValue(selectItems.find(val => val.id === this.field.control.value))
              }

              if (typeof this.field.control.value === "object" && this.field.control.value.id) {
                this.field.control.setValue(selectItems.find(val => val.id === this.field.control.value.id))
              }
            }

            if (this.field.controlType == "ddlMultiple" && this.field.control.value.length > 0) {
              let ids = [];
              if (typeof (this.field.control.value[0]) == "number" || typeof (this.field.control.value[0]) == "string") {
                ids = [...this.field.control.value];
              }
              else {
                ids = this.field.control.value.map((sel: SelectListItem) => sel.id);
              }
              this.field.control.setValue(selectItems.filter(val => ids.includes(val.id)));
            }
          }
          else {
            if (defaultItem) {
              this.field.control.setValue(this.field.controlType == "ddl" ? defaultItem : [defaultItem]);
            }
          }

          return selectItems;
        })
    );

    if (this.field.controlType === "ddlMultiple") {
      this.field.control.valueChanges.subscribe((items: SelectListItem[]) => {
        this.ddlMultipleText = items?.length > 0 ? items[0].text : '';
      })
    }
  }

  public clearInput() {
    if (this.field.controlType == "ddl") {
      this.field.control.setValue(null);
    }
    else if (this.field.controlType == "ddlMultiple") {
      this.field.control.setValue([]);
    }
  }

  public canShowClearBtn(): boolean {
    if (this.field.editable == false) {
      return false;
    }
    if (this.field.controlType == 'ddl') {
      return !!this.field.control.value && !!this.field.control.value?.id;
    }
    else if (this.field.controlType == 'ddlMultiple') {
      return this.field.control.value?.length != 0;
    }
    return false;
  }
}
